// SCRRequestButton.jsx — F3 (D9.5) — botão "Solicitar Carta SCR" pro analista
// UX revista 2026-04-29: default = email automático pro cadastro.
// Checkbox "Apenas gerar link" pra casos sem envio. Botão "Editar" pra trocar destinatário.

import React, { useState } from "react";

const C = {
  azure: "#1a3f6f",
  green: "#0f7a3f",
  red: "#dc2626",
  amber: "#f59e0b",
  gray: "#6b7280",
  card: "#fff",
  border: "#e5e7eb",
};

const PRS_BASE = (typeof window !== "undefined" && window.__PRS_BASE__) || "https://api.financialmind.ai";

export default function SCRRequestButton({
  tenantId,
  cnpj,
  responsavelEmail,
  responsavelNome,
  empresaNome,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [lifetimeDays, setLifetimeDays] = useState(7);

  // UX: default vai pro email do cadastro. "Editar" expõe campos. "Apenas gerar link" desliga email.
  const [editing, setEditing] = useState(false);          // mostra inputs editáveis
  const [skipEmail, setSkipEmail] = useState(false);      // não enviar email (só link)
  const [emailOverride, setEmailOverride] = useState(""); // se editing, valor custom
  const [nomeOverride, setNomeOverride] = useState("");

  const emailFinal = editing ? emailOverride : (responsavelEmail || "");
  const nomeFinal = editing ? nomeOverride : (responsavelNome || "");
  const semEmailDoCadastro = !responsavelEmail;

  const generate = async () => {
    /* NAO MANDAR O QUE NAO SE SABE — 23/09/2026.
       Este componente recebia `tenantId` com fallback `|| "default"` do lado de
       quem chama. O literal viajava ate o servico de PRS, que respondia
       `tenant_id invalido` — e o usuario via, sobre um botao de "solicitar
       carta SCR", um erro tecnico de outro servico a respeito de um campo que
       ele nunca preencheu. Agora, sem tenant, o pedido nao sai, e a tela diz o
       que de fato aconteceu e o que fazer. */
    if (!tenantId) {
      setError("Sessão sem identificação do tenant. Saia e entre de novo; se persistir, é cadastro do usuário sem empresa vinculada.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${PRS_BASE}/scr/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_id: tenantId,
          cnpj,
          lifetime_days: lifetimeDays,
          send_email: !skipEmail && Boolean(emailFinal),
          responsavel_email: !skipEmail ? (emailFinal || null) : null,
          responsavel_nome: nomeFinal || null,
          empresa_nome: empresaNome || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!data?.url) return;
    try {
      await navigator.clipboard.writeText(data.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const reset = () => {
    setData(null); setError(null); setOpen(false);
    setEditing(false); setSkipEmail(false);
    setEmailOverride(""); setNomeOverride("");
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          marginTop: 8, padding: "6px 12px", background: C.azure, color: "#fff",
          border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600,
        }}>
        📤 Solicitar Carta SCR ao tomador
      </button>
    );
  }

  return (
    <div style={{
      marginTop: 8, padding: 12, background: "#f9fafb",
      border: `1px solid ${C.border}`, borderRadius: 8,
    }}>
      {!data && !loading && (
        <>
          <div style={{ fontSize: 12, marginBottom: 10, color: C.gray }}>
            Gera link único pro tomador <b>{cnpj}</b> fazer upload da carta SCR.
            {!skipEmail && " Email automático será enviado via Resend."}
          </div>

          {/* Email destinatário */}
          {!skipEmail && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: C.gray, marginBottom: 4 }}>
                Email destinatário:
              </div>
              {!editing ? (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "6px 8px",
                  background: "#fff", border: `1px solid ${C.border}`, borderRadius: 4,
                }}>
                  <span style={{
                    flex: 1, fontSize: 12, fontFamily: "monospace",
                    color: semEmailDoCadastro ? C.red : C.azure,
                  }}>
                    {responsavelEmail || "⚠ Sem email no cadastro — clique 'Editar'"}
                  </span>
                  <button onClick={() => {
                    setEditing(true);
                    setEmailOverride(responsavelEmail || "");
                    setNomeOverride(responsavelNome || "");
                  }} style={{
                    fontSize: 10, padding: "2px 8px", background: "transparent",
                    border: `1px solid ${C.border}`, borderRadius: 4, color: C.gray, cursor: "pointer",
                  }}>Editar</button>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 6 }}>
                  <input
                    type="email"
                    placeholder="email do responsável (ex: contato@empresa.com.br)"
                    value={emailOverride}
                    onChange={e => setEmailOverride(e.target.value)}
                    style={{
                      padding: "6px 8px", fontSize: 11, border: `1px solid ${C.border}`,
                      borderRadius: 4, width: "100%",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="nome do responsável (opcional)"
                    value={nomeOverride}
                    onChange={e => setNomeOverride(e.target.value)}
                    style={{
                      padding: "6px 8px", fontSize: 11, border: `1px solid ${C.border}`,
                      borderRadius: 4, width: "100%",
                    }}
                  />
                  <button onClick={() => setEditing(false)} style={{
                    fontSize: 10, padding: "2px 8px", background: "transparent",
                    border: `1px solid ${C.border}`, borderRadius: 4, color: C.gray, cursor: "pointer",
                    alignSelf: "flex-start",
                  }}>← Voltar pro email do cadastro</button>
                </div>
              )}
            </div>
          )}

          {/* Toggle "Apenas gerar link" */}
          <label style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 11, color: C.gray, marginBottom: 10, cursor: "pointer",
          }}>
            <input
              type="checkbox"
              checked={skipEmail}
              onChange={e => setSkipEmail(e.target.checked)}
            />
            Apenas gerar link (não enviar email automático)
          </label>

          {/* Validade */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: C.gray }}>Validade do link:</label>
            <select
              value={lifetimeDays}
              onChange={e => setLifetimeDays(+e.target.value)}
              style={{ padding: "4px 8px", fontSize: 11, border: `1px solid ${C.border}`, borderRadius: 4 }}>
              <option value={3}>3 dias</option>
              <option value={7}>7 dias (recomendado)</option>
              <option value={15}>15 dias</option>
              <option value={30}>30 dias</option>
            </select>
          </div>

          {/* Ações */}
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={generate}
              disabled={!skipEmail && !emailFinal}
              style={{
                padding: "6px 12px",
                background: !skipEmail && !emailFinal ? "#d1d5db" : C.green,
                color: "#fff", border: "none",
                borderRadius: 6,
                cursor: !skipEmail && !emailFinal ? "not-allowed" : "pointer",
                fontSize: 11, fontWeight: 600,
              }}>
              {skipEmail ? "Gerar link" : "Gerar e enviar email"}
            </button>
            <button onClick={reset} style={{
              padding: "6px 12px", background: "transparent", color: C.gray, border: `1px solid ${C.border}`,
              borderRadius: 6, cursor: "pointer", fontSize: 11,
            }}>Cancelar</button>
          </div>
        </>
      )}

      {loading && (
        <div style={{ fontSize: 12, color: C.gray }}>
          ⏳ Gerando token{!skipEmail ? " e enviando email" : ""}...
        </div>
      )}

      {error && (
        <div style={{ fontSize: 12, color: C.red, padding: 8, background: "#fee2e2", borderRadius: 4, marginTop: 4 }}>
          ❌ {error}
        </div>
      )}

      {data && (
        <div>
          {/* Status do email */}
          {data.email?.status === "sent" && (
            <div style={{
              background: "#dcfce7", border: `1px solid ${C.green}`, borderRadius: 4,
              padding: 8, fontSize: 12, color: "#166534", marginBottom: 10,
            }}>
              ✅ Email enviado pra <b>{emailFinal}</b>
              <span style={{ display: "block", fontSize: 10, color: "#15803d", marginTop: 2 }}>
                ID Resend: {data.email.email_id}
              </span>
            </div>
          )}
          {data.email?.status === "failed" && (
            <div style={{
              background: "#fef3c7", border: `1px solid ${C.amber}`, borderRadius: 4,
              padding: 8, fontSize: 12, color: "#92400e", marginBottom: 10,
            }}>
              ⚠️ Email falhou — link gerado mas não enviado:<br/>
              <span style={{ fontSize: 11 }}>{data.email.error}</span>
            </div>
          )}
          {data.email?.status === "skipped" && (
            <div style={{ fontSize: 11, color: C.gray, marginBottom: 8 }}>
              ℹ️ Email não enviado. Use o link abaixo manualmente.
            </div>
          )}

          <div style={{ fontSize: 11, color: C.green, fontWeight: 600, marginBottom: 8 }}>
            ✅ Link válido até {new Date(data.expires_at).toLocaleString("pt-BR")}
          </div>
          <div style={{
            background: "#fff", border: `1px solid ${C.border}`, borderRadius: 4,
            padding: 8, fontSize: 11, fontFamily: "monospace", color: C.gray,
            wordBreak: "break-all", marginBottom: 8, maxHeight: 80, overflowY: "auto",
          }}>
            {data.url}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button onClick={copyLink} style={{
              padding: "6px 12px", background: C.azure, color: "#fff", border: "none",
              borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600,
            }}>{copied ? "✓ Copiado" : "📋 Copiar link"}</button>
            <button onClick={reset} style={{
              padding: "6px 12px", background: "transparent", color: C.gray, border: `1px solid ${C.border}`,
              borderRadius: 6, cursor: "pointer", fontSize: 11,
            }}>Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
}
